package com.swetdot.swetgram.ui.design

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import com.swetdot.swetgram.ui.design.SwetBasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.pluralStringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chatlist_row_unread_description

/**
 * The app chrome: top bar, bottom navigation, cards, search, list rows.
 *
 * Everything here is hand-rolled rather than themed on top of Material3, because in each case
 * Material's own defaults contradict the SDS in a way the theme cannot reach:
 *
 *  - `NavigationBar` is 80dp tall against SDS §11.6's 64dp, and `NavigationBarItem` styles its
 *    label from `labelMedium` (13sp) where §5.3 puts tabs at `label.large` (15sp/600). It also
 *    paints a pill indicator behind the active icon, which §11.6 does not ask for -- the spec
 *    says the active tab is *accent blue*, not that it grows a container.
 *  - `TopAppBar` is `ExperimentalMaterial3Api`, carries a scroll-elevation overlay that §8.3
 *    rules out, and centres nothing the way §11.7 describes.
 *  - `OutlinedTextField` enforces a 56dp minimum, so it cannot be the 44-48dp search bar of
 *    §11.3.
 *
 * Icons come from `SwetIcons.kt` -- §9.1's Swetgram-owned set, one 24dp grid and one stroke width
 * across all of it. The chrome no longer draws its own: the `SwetGlyph` enum and `SwetGlyphIcon`
 * that used to live in this file are gone, and every call site here uses [SwetIcon].
 */

/** SDS §11.7. 56dp, the standard bar height, expressed from the touch-target scale. */
private val TopBarHeight: Dp = TouchTarget.recommended + Space.one

/** The inset that puts a 44dp icon button's glyph on the same optical line as a 16dp margin. */
private val IconButtonInset: Dp = Space.half

/** What is left of the screen margin once a 44dp icon button is not there to fill it. */
private val TitleInsetWithoutIcon: Dp = Space.one + Space.half

// -------------------------------------------------------------------------------------------
// Pure logic. Extracted so it can be tested without a renderer -- see ChromeLogicTest.
// -------------------------------------------------------------------------------------------

/** Above this the badge reads `99+`; a four-digit count would squeeze the row's title. */
internal const val BADGE_MAX_COUNT: Int = 99

/**
 * The text of an unread badge, or null when there is nothing to show.
 *
 * Null rather than an empty string so the call site cannot accidentally draw an empty circle:
 * `formatBadgeCount(0)` has to be handled, it cannot be rendered by mistake. Non-positive
 * counts collapse to null together -- a negative count is a bug upstream, and drawing "-1" on a
 * conversation row is a worse outcome than drawing nothing.
 */
internal fun formatBadgeCount(count: Int): String? = when {
    count <= 0 -> null
    count > BADGE_MAX_COUNT -> "$BADGE_MAX_COUNT+"
    else -> count.toString()
}

/**
 * How a list row draws its unread badge, as a NAMED STATE rather than three loose colours.
 *
 * [Accent] is SDS §12.1's badge and what every list in the app has always drawn: the accent pill with
 * a white number. [Inverted] is the chat-list mockup's -- "a CIRCLE with a WHITE FILL and a BLACK
 * number in it, around 22-24dp. Not accent blue." -- and it is a style rather than two colour
 * parameters so that a caller cannot pick a fill without the foreground that goes with it. That is
 * the mistake `avatarInitialsColor` exists to stop one component further down.
 */
enum class SwetBadgeStyle { Accent, Inverted }

/** The four values a [SwetBadgeStyle] resolves to. See [swetBadgeColors]. */
internal data class SwetBadgeColors(
    val container: Color,
    val content: Color,
    /**
     * The colour of the row's TIMESTAMP when this badge is beside it.
     *
     * A FIELD OF ITS OWN, WHICH IT WAS NOT. The timestamp used to be drawn in [container] -- the
     * badge's fill -- so that "the timestamp matches the badge" was true by construction rather
     * than by two call sites agreeing. That invariant is worth keeping and this keeps it: there is
     * still ONE function deciding both, and a screen still cannot set them apart.
     *
     * What changed is that [container] became a colour a 12sp label cannot be drawn in. The accent
     * is a fill now -- #E51E1E on `card` measures 4.23:1 -- so an unread row was about to print its
     * time in red text that fails AA. Splitting the field is what let the fill stay red while the
     * text goes white, instead of choosing between a legible timestamp and a visible badge.
     */
    val timestamp: Color,
    val minSize: Dp,
)

/**
 * A [SwetBadgeStyle] against a palette.
 *
 * Pure, and extracted for the reason `swetTextButtonColor` is: this host has no renderer, so a
 * colour chosen inside a composable cannot be asserted, and every rule in `DesignSystemRulesTest`
 * shipped wrong at least once while the comment beside it said the right thing.
 *
 * [Inverted]'s pair is `text.primary` on the fill and `text.inverse` on the number, WHICH INVERTS
 * WITH THE THEME AND IS SUPPOSED TO. The mockup is a dark screen, so it asks for white-on-black. In
 * the light theme those two tokens swap, giving a near-black disc with a white number -- the same
 * relationship (the badge is the inverted, highest-contrast thing on the row) rather than the same
 * two hexes, which is exactly what `ChatColors.outgoingBubble` does with the same pair and for the
 * same reason. Fixing the fill at literal white would put white on #FAFAFA in the light theme.
 */
internal fun swetBadgeColors(style: SwetBadgeStyle, colors: SwetgramColors): SwetBadgeColors =
    when (style) {
        SwetBadgeStyle.Accent -> SwetBadgeColors(
            container = colors.accent,
            // White on the accent, as everywhere else drawn on top of it (see ChatColors.onAccent).
            content = colors.onAccent,
            // NOT the fill. See [SwetBadgeColors.timestamp]: the accent is a fill colour and
            // cannot carry 12sp text. Emphasis by weight instead -- an unread row's time is the
            // BRIGHT one, which is the same statement the red was making.
            timestamp = colors.textPrimary,
            minSize = Sizes.unreadBadge,
        )

        SwetBadgeStyle.Inverted -> SwetBadgeColors(
            container = colors.textPrimary,
            content = colors.textInverse,
            // Identical to the fill here, and left as its own expression rather than reusing
            // `container`: the two are the same value for this style by coincidence, not by rule.
            timestamp = colors.textPrimary,
            minSize = Sizes.unreadBadgeLarge,
        )
    }

/** DECISIONS §5 caps a message at 4096 characters; a row preview shows a small fraction of it. */
internal const val PREVIEW_MAX_CHARS: Int = 120

private val WhitespaceRun = Regex("\\s+")

/**
 * Flattens a message body into the single line a conversation row shows.
 *
 * `maxLines = 1` plus `TextOverflow.Ellipsis` is not enough on its own for two reasons:
 *
 *  - A body that begins with newlines renders as a blank preview, because the first line is
 *    genuinely empty. Collapsing whitespace first is what makes "\n\nhi" read as "hi".
 *  - The full 4096-character body is still measured and laid out before it is clipped, on every
 *    row, on every recomposition of the list.
 *
 * The cut is surrogate-safe: slicing between the halves of a pair yields half a code point,
 * which renders as an empty box rather than an emoji.
 */
internal fun singleLinePreview(raw: String, maxChars: Int = PREVIEW_MAX_CHARS): String {
    if (maxChars <= 0) return ""
    val collapsed = raw.replace(WhitespaceRun, " ").trim()
    if (collapsed.length <= maxChars) return collapsed
    val end = if (collapsed[maxChars - 1].isHighSurrogate()) maxChars - 1 else maxChars
    return collapsed.substring(0, end) + "…"
}

/**
 * A 44dp circular icon button -- SDS §6.3's minimum, which a 24dp glyph does not reach on its
 * own.
 *
 * [contentDescription] is required and belongs to the BUTTON, not to the glyph: the icon slot
 * should stay decorative so the node is announced once.
 *
 * Pressed feedback is §25.1's state layer at `motion.instant` (80ms), applied AFTER the clip so
 * the tint is the circle it is drawn inside rather than the 44dp square it is laid out in.
 */
@Composable
fun SwetIconButton(
    contentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    /**
     * What the icon slot is offered, or null for the default `text.primary`.
     *
     * THE DISABLED COLOUR IS NOT OVERRIDABLE, and that asymmetry is the point: a caller may choose
     * the resting emphasis of its own mark -- the Discover feed's «···» is grey, alongside the grey
     * byline it sits beside -- but nothing may make a disabled control look enabled. §25.1 gives
     * disabled its own token and this button is the only thing on screen that knows the state.
     *
     * IT EXISTS BECAUSE A CALLER WAS ALREADY DOING THIS BY HAND. `PostOverflowControl` took the
     * tint the slot offered and threw it away -- `MoreGlyph(tint ?: buttonTint)` -- which worked,
     * was commented as the only icon button in the app that declined the offer, and quietly opted
     * out of the disabled colour as well. A parameter makes the intent legible and keeps the one
     * rule that is not the caller's to bend.
     */
    tint: Color? = null,
    icon: @Composable (tint: Color) -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val description = contentDescription
    Box(
        modifier = modifier
            .size(TouchTarget.minimum)
            .clip(CircleShape)
            .swetPressable(
                onClick = onClick,
                enabled = enabled,
                role = Role.Button,
                feedback = SwetPressFeedback.StateLayer,
            )
            .semantics { this.contentDescription = description },
        contentAlignment = Alignment.Center,
    ) {
        icon(if (enabled) tint ?: colors.textPrimary else colors.textTertiary)
    }
}

/**
 * The house screen title: the screen's name, large, on the canvas, with at most one action beside
 * it.
 *
 * NOT [SwetTopAppBar], and the difference is not cosmetic. That component is §11.7's 56dp bar on a
 * `surface` fill with an optional divider under it -- a TOOLBAR. The mockups this serves read as a
 * screen title instead: "the word «Чаты» large, white, bold, left-aligned on the black -- clearly a
 * screen title rather than a toolbar label; it reads around 28-32sp", on a canvas with "no app-bar
 * surface, no divider under the title". Bending the shared bar into that would have left every
 * screen's chrome carrying two shapes under one name.
 *
 * `headlineLarge` is 28sp/Bold -- the bottom of the mockup's stated band and an EXISTING §5.3 role
 * (`title.large`, "main screen title") rather than a size invented between two rungs.
 *
 * IT WAS BUILT THREE TIMES BEFORE IT WAS BUILT ONCE. `ChatsHeader`, `GroupsHeader` and
 * `DiscoverHeader` were three private composables in three files, identical down to the
 * `defaultMinSize(TouchTarget.recommended + Space.one)` that makes the three tabs' titles share a
 * baseline when a user switches between them -- a property no one file can hold. Two of them also
 * derived [HeaderActionInset] privately and identically.
 *
 * THE END PADDING DEPENDS ON WHETHER THERE IS AN ACTION, which is the whole reason that inset
 * exists: a 24dp glyph inside a 44dp round target would otherwise sit 26dp from the screen edge
 * against the title's 16dp. With no action the ordinary margin is right, because there is nothing
 * inside a component to compensate for.
 *
 * @param action a trailing control, conventionally one [SwetIconButton]. Null draws nothing and
 *   takes the plain margin -- the Discover feed passes null because its mockup is explicit that the
 *   right of the title is empty, and a slot that no call site fills is the shape of thing this
 *   repository keeps finding six months later still believed in.
 * @param maxLines how many lines the title may take before it ellipsizes. ONE BY DEFAULT, WHICH IS
 *   RIGHT FOR EVERY CALLER THIS COMPONENT HAD AND WRONG FOR THE ONE THAT JUST ARRIVED. The four tabs
 *   and Profile pass a single word -- Chats, Discover, Groups, Profile -- which cannot wrap at any
 *   font scale, and truncating is what keeps a long one from pushing an action off the end of the
 *   row. The auth screens pass a SENTENCE ("Create your account", "Reset your password") with
 *   nothing beside it, inside a column that scrolls: at a large system font "Create your account"
 *   ellipsizes to something that no longer names the screen, and the only cost of letting it wrap is
 *   a taller header on a screen that has room for one. A ceiling rather than the wrap itself, so a
 *   caller with an action beside the title still cannot be squeezed out by a long word.
 */
@Composable
fun SwetScreenTitle(
    title: String,
    modifier: Modifier = Modifier,
    action: (@Composable () -> Unit)? = null,
    maxLines: Int = 1,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.recommended + Space.one)
            .padding(
                start = Space.screenMargin,
                end = if (action != null) HeaderActionInset else Space.screenMargin,
                top = Space.one,
                bottom = Space.one,
            ),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.headlineLarge,
            color = colors.textPrimary,
            maxLines = maxLines,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        action?.invoke()
    }
}

// -------------------------------------------------------------------------------------------
// Top app bar
// -------------------------------------------------------------------------------------------

/**
 * SDS §11.7. A calm, functional bar: title on the left, no logo, no hamburger, no decorative
 * height.
 *
 * Window insets are NOT applied here. The app frame in `ui/Navigation.kt` insets every screen
 * once -- `statusBarsPadding()` on its root `Column`, and `safeDrawing`'s HORIZONTAL sides on the
 * `NavHost` inside it -- so a bar that added `statusBarsPadding()` of its own would pad twice,
 * which only shows up on a device and never in a preview. (There is no `Scaffold`; that frame is
 * a `Column` precisely because `Scaffold`'s own insets were wrong for the composer. See the
 * comment above `SwetgramApp`.) The horizontal inset on the `NavHost` is what keeps this bar's
 * trailing actions clear of a landscape cutout or gesture strip.
 *
 * [showDivider] defaults on, but the Chats screen wants it off: §11.7 puts the search bar
 * directly under the title, and a hairline between the two reads as a seam.
 */
@Composable
fun SwetTopAppBar(
    title: String,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
    onBack: (() -> Unit)? = null,
    backContentDescription: String = "Back",
    showDivider: Boolean = true,
    actions: @Composable RowScope.() -> Unit = {},
) {
    val colors = SwetgramThemeTokens.colors

    Surface(color = colors.surface, modifier = modifier) {
        Column {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(TopBarHeight)
                    .padding(horizontal = IconButtonInset),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (onBack != null) {
                    SwetIconButton(
                        contentDescription = backContentDescription,
                        onClick = onBack,
                    ) { tint ->
                        SwetIcon(SwetIcons.ChevronLeft, tint, contentDescription = null)
                    }
                    Spacer(Modifier.width(Space.half))
                } else {
                    Spacer(Modifier.width(TitleInsetWithoutIcon))
                }

                Column(Modifier.weight(1f)) {
                    Text(
                        text = title,
                        // §5.3 `headline`, 20sp/600 -- the slot the SDS gives screen headers.
                        style = MaterialTheme.typography.titleLarge,
                        color = colors.textPrimary,
                        // A long group name must truncate rather than push the actions off the
                        // end of the bar.
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    if (subtitle != null) {
                        Text(
                            text = subtitle,
                            style = MaterialTheme.typography.labelMedium,
                            color = colors.textSecondary,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.End,
                    content = actions,
                )
            }

            if (showDivider) {
                HorizontalDivider(thickness = Sizes.borderHairline, color = colors.border)
            }
        }
    }
}

// -------------------------------------------------------------------------------------------
// Bottom navigation
// -------------------------------------------------------------------------------------------

/**
 * One tab of [SwetBottomNav].
 *
 * The icon is a slot taking its tint, so the design system does not have to know which glyphs
 * the app's navigation uses -- `ui/TabIcons.kt` owns those.
 */
data class SwetNavTab(
    val label: String,
    val icon: @Composable (tint: Color) -> Unit,
)

/**
 * How much room [SwetBottomNav] takes out of the bottom of the window, insets included.
 *
 * A screen whose content scrolls UNDER the floating bar reserves this in its `contentPadding`. It is
 * [floatingNavHeight] plus the live system inset, read here because `WindowInsets` is only reachable
 * from a composable and the arithmetic is deliberately not.
 *
 * EVERY TAB APPLIES IT ITSELF, AND THE APP FRAME APPLIES IT NOWHERE. This used to read "two callers
 * and they must not both apply it": the inbox reserved its own and `ui/Navigation.kt` padded the
 * `NavHost` for every other tab. That split is what made one bar look like two components -- on the
 * padded tabs the screen stopped ABOVE the bar and the bar floated on a bare strip of frame colour,
 * while on the inbox the canvas ran to the bottom edge and the rows scrolled under it. The host
 * padding is gone; each tab's scrollable takes this in its own `contentPadding`, and each tab's
 * non-scrolling branches (skeleton, empty state) take it as plain bottom padding because there is no
 * `contentPadding` to put it in. `ui/Navigation.kt` records the removal.
 *
 * SO IT IS STILL "NOT TWICE", just from the other direction: a screen that also applies
 * `navigationBarsPadding` has counted the system gesture inset twice, because this value already
 * contains it. `GroupDiscoverScreen` takes the plain `navigationBarsPadding` INSTEAD of this, for
 * the opposite reason -- it is a pushed detail route with no bar drawn over it at all.
 */
@Composable
fun swetFloatingNavClearance(): Dp =
    FloatingNavVerticalInset * 2 + swetBottomNavContentHeight() +
        WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding()

@Composable
private fun swetBottomNavContentHeight(): Dp {
    val fontScale = LocalDensity.current.fontScale
    val largeTextAllowance = (16f * (fontScale - 1f).coerceAtLeast(0f)).dp
    return TouchTarget.bottomNavHeight + largeTextAllowance
}

/**
 * SDS §11.6, REDRAWN TO THE CHAT-LIST MOCKUP -- see DECISIONS.md §26.
 *
 * §11.6 describes a full-width bar attached to the bottom edge, 64dp tall, with a hairline above it
 * and the active tab in `accent.blue`. The mockup keeps the height and the four always-visible
 * labels and changes three things, and the user asked for the mockup:
 *
 *  - it FLOATS. A rounded container inset from the left, right and bottom, so the canvas runs behind
 *    it on all four sides. The insets and the radius are [FloatingNavHorizontalInset],
 *    [FloatingNavVerticalInset] and [Radius.xl], and the reservation a screen underneath needs is
 *    [swetFloatingNavClearance] rather than a number written per screen.
 *  - the ACTIVE TAB IS WHITE, not `accent.blue`. That is `text.primary`, so it inverts with the
 *    theme; the inactive tabs stay `text.secondary` exactly as §11.6 has them.
 *  - the labels are `caption` 12sp rather than §5.3's `label.large` 15sp. The mockup reads "around
 *    11sp" and the type scale has no 11sp rung; 12sp is the nearest one that exists, and adding a
 *    twelfth type role for a 1sp difference would be inventing precision the image cannot support.
 *
 * The hairline is GONE with the full-width bar it belonged to. A divider above a container that is
 * already separated from the screen by 8dp of canvas would be a seam drawn across thin air.
 *
 * It is still a `Row` and not a `NavigationBar`, for the reasons this file's header gives: Material
 * would ship 80dp tall with 13sp labels and a pill indicator behind the active icon.
 *
 * `navigationBarsPadding` and the horizontal `safeDrawing` inset are applied OUTSIDE the container
 * now rather than inside it. With a full-width bar the colour had to run behind the gesture bar
 * while the content sat above it; a floating container is meant to stop short of both, so the insets
 * push the whole container up and in. Nothing paints the strip underneath, which is the point --
 * whatever the screen's canvas is shows through, and on the inbox that is pure black.
 */
@Composable
fun SwetBottomNav(
    tabs: List<SwetNavTab>,
    selectedIndex: Int,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = modifier
            .fillMaxWidth()
            .navigationBarsPadding()
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal))
            .padding(
                horizontal = FloatingNavHorizontalInset,
                vertical = FloatingNavVerticalInset,
            )
            // Clipped BEFORE the fill, so the container's corners are the corners of everything
            // inside it -- including each tab's own pressed state layer, which is a plain rectangle
            // and would otherwise square off the two end tabs.
            .clip(RoundedCornerShape(Radius.xl))
            .background(colors.card)
            // THE EDGE, AND IT IS NOT DECORATION. The full-width bar this replaced had an explicit
            // boundary: a `HorizontalDivider` in `border` sitting above it. That divider went with
            // the full-width bar (a rule floating 8dp off the canvas would be drawn across thin
            // air), and the fill was left to do the separating on its own. It cannot, in the light
            // theme: `card` #F3F4F6 on `background` #FAFAFA measures 1.05:1, which is not a visible
            // boundary by any measure -- WCAG 1.4.11 asks 3:1 of the edge that identifies a control,
            // and 1.05 means four unbounded labels floating over the page. Dark is thin but real at
            // 1.32:1 on the inbox's #000000. A hairline in `border` is what both themes have that
            // the fill does not, and it is the same token the deleted divider used.
            .border(Sizes.borderHairline, colors.border, RoundedCornerShape(Radius.xl))
            .height(swetBottomNavContentHeight()),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        tabs.forEachIndexed { index, tab ->
            val isSelected = index == selectedIndex
            // The mockup: "The active tab is WHITE, not accent blue. Inactive tabs are grey."
            //
            // §19.4 lists "Tab transitions" under what motion is for, and this is the whole of the
            // transition -- the active tab IS a colour, it does not grow an indicator to slide. So
            // the tint crosses over `motion.fast` (150ms, "state changes") and nothing moves.
            val tint by animateColorAsState(
                targetValue = if (isSelected) colors.textPrimary else colors.textSecondary,
                animationSpec = swetTween(Motion.fast, SwetEasing.enter),
                label = "tabTint",
            )

            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .swetPressable(
                        onClick = { onSelect(index) },
                        role = Role.Tab,
                        feedback = SwetPressFeedback.StateLayer,
                    )
                    // Announced as selected, not merely coloured differently: colour alone is
                    // invisible to a screen reader (§22).
                    .semantics { selected = isSelected }
                    // Half-step, not a full one: at 64dp a full 8dp top and bottom leaves exactly
                    // 48dp, which a 24dp glyph plus a label fills with nothing to spare once the
                    // system font scale goes up.
                    .padding(vertical = Space.half),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
            ) {
                tab.icon(tint)
                Spacer(Modifier.height(Space.half))
                Text(
                    text = tab.label,
                    // §5.3 `caption` 12sp, the nearest existing rung to the mockup's ~11sp. Passed
                    // EXPLICITLY: this is the one the reviewer catches, because a bare Text inside
                    // a nav item inherits 13sp and looks intentional.
                    style = MaterialTheme.typography.labelSmall,
                    color = tint,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(Modifier.height(Sizes.borderHairline * 2))
                Box(
                    modifier = Modifier.size(
                        width = Space.two,
                        height = Sizes.borderHairline * 2,
                    ),
                    contentAlignment = Alignment.Center,
                ) {
                    if (isSelected) {
                        Box(
                            Modifier
                                .size(width = Space.two, height = Sizes.borderHairline * 2)
                                .clip(CircleShape)
                                .background(colors.textPrimary),
                        )
                    }
                }
            }
        }
    }
}

// -------------------------------------------------------------------------------------------
// Card
// -------------------------------------------------------------------------------------------

/**
 * SDS §11.4. 16dp radius, card surface, 16dp padding, no shadow (§8.3 keeps shadows for FABs
 * and overlays).
 *
 * The border is opt-in because §11.4 calls it optional and §11.4's rules ask for spacing over
 * dividers: a screen of bordered cards is exactly the "heavy" the spec is warning about.
 */
@Composable
fun SwetCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    showBorder: Boolean = false,
    contentPadding: PaddingValues = PaddingValues(Space.two),
    content: @Composable ColumnScope.() -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val shape = RoundedCornerShape(Radius.md)

    Column(
        modifier = modifier
            // Clipped before the click, so the press indication stops at the corners instead of
            // painting a square over them.
            .clip(shape)
            .background(colors.card)
            .then(
                if (showBorder) Modifier.border(Sizes.borderHairline, colors.border, shape)
                else Modifier,
            )
            .then(
                if (onClick != null) {
                    Modifier
                        // §25.1's pressed state, at §19.2's `motion.instant`. A card is a
                        // surface, so it tints rather than scales -- see swetPressStateLayer.
                        .swetPressable(
                            onClick = onClick,
                            role = Role.Button,
                            feedback = SwetPressFeedback.StateLayer,
                        )
                        .defaultMinSize(minHeight = TouchTarget.minimum)
                } else {
                    Modifier
                },
            )
            .padding(contentPadding),
        content = content,
    )
}

// -------------------------------------------------------------------------------------------
// Search bar
// -------------------------------------------------------------------------------------------

/**
 * SDS §11.3. 48dp tall, 20dp radius, card background, leading search glyph, placeholder
 * `Search`.
 *
 * `BasicTextField` rather than [SwetTextField]: §11.2's field is 52dp and Material's own
 * enforces 56dp, so neither can be squeezed into §11.3's 44-48dp band without fighting a
 * minimum. What is lost is the label animation, which a search bar does not have anyway.
 *
 * Clearing is handled here rather than by the caller -- the bar already owns the value, so a
 * separate `onClear` would be one more thing to forget to wire.
 *
 * [focusRequester] is how a screen whose ONLY purpose is searching opens with the keyboard up,
 * as §11.3 requires ("tapping search opens active search state; keyboard appears immediately").
 * It is a parameter rather than an `autoFocus` flag because the design system should not decide
 * WHEN a screen wants focus: the Chats tab must not steal it -- the list underneath is the point
 * of that screen -- while New Chat has nothing else to offer. Attaching it to the inner field is
 * the part the caller cannot do, since `Modifier.focusRequester` on the bar's own row would name
 * a node that is not a text field.
 */
@Composable
fun SwetSearchBar(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "Search",
    enabled: Boolean = true,
    clearContentDescription: String = "Clear search",
    onSearch: (() -> Unit)? = null,
    focusRequester: FocusRequester? = null,
    /**
     * The field's corner radius. §11.3's own 20dp by default, which on a 48dp field is 4dp short of
     * a pill and is what every search box in the app has drawn.
     *
     * A PARAMETER RATHER THAN A CHANGED DEFAULT, because only one screen was redrawn. The chat-list
     * mockup is explicit that its field is "a ROUNDED RECTANGLE, not a pill" and passes
     * [Radius.field] 12dp; New Chat and the group member search are untouched and still read §11.3.
     * Adopting the mockup's shape app-wide is a one-line change to this default when somebody
     * decides that, rather than a side effect of redrawing the inbox.
     */
    cornerRadius: Dp = Radius.lg,
    backgroundColor: Color? = null,
    fieldTextStyle: androidx.compose.ui.text.TextStyle? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val textStyle = fieldTextStyle ?: MaterialTheme.typography.bodyMedium

    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(TouchTarget.recommended)
            .clip(RoundedCornerShape(cornerRadius))
            .background(backgroundColor ?: colors.card)
            .padding(start = Space.two, end = IconButtonInset),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Decorative: the placeholder already announces "Search", and an icon description
        // beside it would have the screen reader say the word twice.
        SwetIcon(
            icon = SwetIcons.Search,
            tint = colors.textSecondary,
            contentDescription = null,
        )

        Spacer(Modifier.width(Space.one))

        Box(Modifier.weight(1f), contentAlignment = Alignment.CenterStart) {
            if (value.isEmpty()) {
                Text(
                    text = placeholder,
                    style = textStyle,
                    color = colors.textTertiary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            SwetBasicTextField(
                value = value,
                onValueChange = onValueChange,
                enabled = enabled,
                singleLine = true,
                textStyle = textStyle.copy(color = colors.textPrimary),
                cursorBrush = SolidColor(colors.accent),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                keyboardActions = KeyboardActions(onSearch = { onSearch?.invoke() }),
                modifier = Modifier
                    .fillMaxWidth()
                    .then(
                        if (focusRequester != null) {
                            Modifier.focusRequester(focusRequester)
                        } else {
                            Modifier
                        },
                    ),
            )
        }

        if (value.isNotEmpty()) {
            SwetIconButton(
                contentDescription = clearContentDescription,
                onClick = { onValueChange("") },
                enabled = enabled,
            ) { tint ->
                SwetIcon(
                    icon = SwetIcons.Close,
                    tint = tint,
                    contentDescription = null,
                    size = Sizes.iconSmall,
                )
            }
        } else {
            // Keeps the text from running to the very edge, and stops the field's width from
            // jumping by 44dp the moment the first character is typed.
            Spacer(Modifier.width(TitleInsetWithoutIcon))
        }
    }
}

// -------------------------------------------------------------------------------------------
// List row
// -------------------------------------------------------------------------------------------

/**
 * SDS §12.1. The conversation row shell: avatar, title, preview, time, unread badge.
 *
 * 72dp, inside §12.1's 68-76dp band and matching [TouchTarget.chatRowHeight]. `defaultMinSize`
 * rather than a fixed height so a large system font grows the row instead of clipping it.
 *
 * The avatar is a SLOT, not a name: a row can show a group photo, a stack, or [SwetAvatar]. It
 * does not know what a Chat is -- that mapping belongs to the screen.
 *
 * §12.1: no heavy separators. The row draws none; spacing does the separating.
 *
 * The row TINTS on press rather than scaling (§25.1, at §19.2's 80ms `motion.instant`). A 72dp
 * full-width row shrinking by even 4% moves both its edges several pixels, which reads as the
 * list twitching rather than as a press -- scale is for things that read as objects, and a row
 * reads as a surface.
 */
@Composable
fun SwetListRow(
    title: String,
    modifier: Modifier = Modifier,
    preview: String? = null,
    /**
     * The preview's colour, when it is something other than the last message.
     *
     * Null keeps §5.4's secondary text, which is what a message preview is. The one caller that
     * passes anything is the chat list showing "typing…", which is a LIVE state rather than
     * content and is tinted accordingly -- exactly as the row's timestamp already turns accent
     * when there is an unread badge beside it.
     */
    previewColor: Color? = null,
    timestamp: String? = null,
    unreadCount: Int = 0,
    /**
     * What a screen reader says about the badge.
     *
     * THE DEFAULT USED TO BE THE KOTLIN LITERAL `"$it unread"`, and it was the one piece of copy in
     * the app that no lint could see: it never appeared in an XML file, so nothing reported it as an
     * English key with no Russian. Every conversation row and every group row took it, which meant
     * the badge -- the one thing on those rows a sighted reader gets for free and a screen-reader
     * user gets only from this sentence -- stayed English on a Russian phone. Nothing looked wrong.
     *
     * `@Composable` SO THE DEFAULT CAN REACH A RESOURCE. It is invoked inside this row's own
     * composition, so the annotation costs nothing and buys the fix for every caller at once rather
     * than for the two that happened to be edited. `%1$s` NOT `%1$d`: the argument arrives as a
     * `String`, which is the shape `pluralStringResource` takes here.
     *
     * The archive entry still overrides it -- "3 unread in the archive" -- because that badge is a
     * TOTAL across conversations and needs saying differently. That override is the reason this is a
     * lambda at all.
     */
    unreadContentDescription: @Composable (Int) -> String = {
        pluralStringResource(Res.plurals.chatlist_row_unread_description, it, it)
    },
    onClick: (() -> Unit)? = null,
    /**
     * A mark that qualifies the TITLE rather than the row -- today, SPS §10.3's muted bell.
     *
     * It sits after the title inside the title's own row, so it truncates with the title instead
     * of competing with the timestamp column for space, and a long name pushes it off the end
     * rather than pushing the time off. Null draws nothing and costs no layout.
     */
    titleTrailing: (@Composable () -> Unit)? = null,
    /**
     * SDS §12.1's accent pill, or the chat-list mockup's inverted disc. See [SwetBadgeStyle].
     *
     * It also decides what colour the TIMESTAMP takes when there is a badge beside it, which is why
     * this is one parameter and not two: the row has always tinted the time to mark unread, and a
     * screen that changed the badge and forgot the time would leave one accent-blue element on a
     * palette that has none. Tying them makes "the timestamp matches the badge" true by construction
     * instead of by review.
     */
    badgeStyle: SwetBadgeStyle = SwetBadgeStyle.Accent,
    /**
     * How far in a hairline under this row starts, or null for no divider at all.
     *
     * §12.1 asks for spacing rather than separators and every list in the app still obeys that --
     * null is the default and nothing but the redrawn inbox passes anything else. The chat-list
     * mockup draws one, INDENTED so it begins where the text begins rather than at the screen edge:
     * a rule running under the avatar would cut the discs into a column of half circles.
     *
     * The caller supplies the number because only the caller knows how wide its own leading slot is
     * -- see `chatRowDividerIndent`, which derives it from the avatar rather than picking it.
     */
    dividerIndent: Dp? = null,
    leading: @Composable () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val badgeColors = swetBadgeColors(badgeStyle, colors)

    // THE CALLER'S MODIFIER STAYS ON THE ROW, NOT ON THIS COLUMN, and the divider sits outside it.
    // `ChatRow` passes its long-press `combinedClickable` in `modifier`; hung on the Column that
    // would make the 1dp hairline part of the row's tap target and paint the pressed state layer
    // across the separator, so two adjacent pressed rows would bleed into one another. The Column
    // is a bare wrapper and takes no modifier of its own.
    Column {
        Row(
            modifier = modifier
                .fillMaxWidth()
                .then(
                    if (onClick != null) {
                        Modifier.swetPressable(
                            onClick = onClick,
                            role = Role.Button,
                            feedback = SwetPressFeedback.StateLayer,
                        )
                    } else {
                        Modifier
                    },
                )
                .defaultMinSize(minHeight = TouchTarget.chatRowHeight)
                .padding(horizontal = Space.two, vertical = Space.one),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            leading()

            Spacer(Modifier.width(Space.two))

            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = title,
                        // §5.4: the row title is body.medium at 600, which is the `titleMedium` slot.
                        style = MaterialTheme.typography.titleMedium,
                        color = colors.textPrimary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        // Weight rather than fill: the adornment keeps its intrinsic width and the
                        // title gives up the rest, so the mark stays visible next to a long name.
                        modifier = Modifier.weight(1f, fill = false),
                    )
                    if (titleTrailing != null) {
                        Spacer(Modifier.width(Space.half))
                        titleTrailing()
                    }
                }
                if (preview != null) {
                    Spacer(Modifier.height(Space.half))
                    Text(
                        // Flattened first: a body starting with a newline would otherwise render as
                        // a blank line. See [singleLinePreview].
                        text = singleLinePreview(preview),
                        // §5.4: the preview is body.small.
                        style = MaterialTheme.typography.bodySmall,
                        color = previewColor ?: colors.textSecondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }

            val badge = formatBadgeCount(unreadCount)

            // The count the badge should DRAW, which is not the same as the count the row has: while
            // the badge animates out, `unreadCount` is already 0 and `SwetUnreadBadge` would return
            // without drawing anything, so an empty box would fade instead of the pill. Kept in a
            // plain box rather than `mutableStateOf` for the reason `TypingIndicator` gives about its
            // own last sentence -- nothing observes it, the recomposition is driven by `unreadCount`,
            // and writing snapshot state during composition costs a whole extra pass.
            val lastBadge = remember { LastBadgeCount() }
            if (unreadCount > 0) lastBadge.value = unreadCount

            if (timestamp != null || badge != null) {
                Spacer(Modifier.width(Space.one))
                Column(
                    horizontalAlignment = Alignment.End,
                    verticalArrangement = Arrangement.Center,
                ) {
                    if (timestamp != null) {
                        Text(
                            text = timestamp,
                            // §5.4: row time is `caption`. The chat-list mockup reads "around 13sp",
                            // which is `label.medium`; this stays `caption` 12sp because it is the slot
                            // §5.4 names for a row's time and the difference is one point. A rung was
                            // not moved for it, and the mockup's number is inside a font-scale step of
                            // what is drawn.
                            style = MaterialTheme.typography.labelSmall,
                            // The badge's OWN timestamp colour when there is a badge beside it --
                            // one function decides both, so this cannot drift from the disc it sits
                            // above. It is `text.primary` for both styles; see
                            // [SwetBadgeColors.timestamp] for why it is no longer the fill.
                            color = if (badge != null) badgeColors.timestamp else colors.textSecondary,
                            maxLines = 1,
                        )
                    }
                    // §19.2's `motion.fast` (150ms) state change. The badge is the one thing on this
                    // row that changes while the user is looking at it and did not touch it -- a
                    // message arrives, or a read receipt clears the count -- so it is exactly the
                    // case where an instant swap reads as a glitch. It occupies no space when hidden,
                    // so the row's layout is unchanged.
                    AnimatedVisibility(
                        visible = badge != null,
                        enter = swetPopIn(),
                        exit = swetPopOut(),
                    ) {
                        Column(
                            modifier = Modifier.swetTransitionTarget(badge != null),
                            horizontalAlignment = Alignment.End,
                        ) {
                            if (timestamp != null) Spacer(Modifier.height(Space.half))
                            SwetUnreadBadge(
                                count = lastBadge.value,
                                contentDescription = unreadContentDescription(lastBadge.value),
                                style = badgeStyle,
                            )
                        }
                    }
                }
            }
        }

        // INSIDE the row's own column, not emitted by the list, so the rule cannot end up on the
        // wrong side of a section header or be forgotten by one call site out of three. Indented by
        // the caller; see `dividerIndent`.
        if (dividerIndent != null) {
            HorizontalDivider(
                modifier = Modifier.padding(start = dividerIndent),
                thickness = Sizes.borderHairline,
                color = colors.border,
            )
        }
    }
}

/** The last non-zero unread count a row drew, kept alive across the badge's exit animation. */
private class LastBadgeCount(var value: Int = 0)

/**
 * SDS §12.1: "keep unread state clear but not noisy". One disc, capped at `99+`.
 *
 * The description is a parameter because the digits alone are meaningless read aloud -- "3" is
 * not "3 unread" -- and because the string has to be localizable (EN + RU).
 *
 * [style] carries the fill, the foreground AND the diameter together, so the two colours cannot be
 * chosen apart. See [SwetBadgeStyle] and [swetBadgeColors]; the numbers it resolves to are
 * [Sizes.unreadBadge] and [Sizes.unreadBadgeLarge], which used to be `Space.two + Space.half`
 * written inline here.
 */
@Composable
fun SwetUnreadBadge(
    count: Int,
    modifier: Modifier = Modifier,
    /**
     * THE SECOND ENGLISH LITERAL THIS BADGE HAD, and it is the same defect as the one on
     * [SwetListRow]: a default written in Kotlin never reaches an XML file, so no copy lint can see
     * it and nothing reports it as untranslated.
     *
     * It is unreachable today -- [SwetListRow] is the only caller and it always passes a sentence --
     * which is precisely why it was worth closing rather than leaving. An unreachable English default
     * on a shared component is a trap for the second caller, not a dead branch: whoever adds one gets
     * a badge that reads correctly in English and says nothing translatable in Russian, and nothing
     * on the screen looks wrong.
     */
    contentDescription: String = pluralStringResource(
        Res.plurals.chatlist_row_unread_description,
        count,
        count,
    ),
    style: SwetBadgeStyle = SwetBadgeStyle.Accent,
) {
    val text = formatBadgeCount(count) ?: return
    val description = contentDescription
    val badge = swetBadgeColors(style, SwetgramThemeTokens.colors)

    Box(
        modifier = modifier
            .defaultMinSize(minWidth = badge.minSize, minHeight = badge.minSize)
            .clip(CircleShape)
            .background(badge.container)
            .padding(horizontal = Space.half)
            // `clearAndSetSemantics`, not `semantics`: the digits inside are already a text
            // node, so merging would have the row announce "3 unread" and then "3".
            .clearAndSetSemantics { this.contentDescription = description },
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = text,
            // §5.3 `caption` -- counters live here.
            style = MaterialTheme.typography.labelSmall,
            color = badge.content,
            maxLines = 1,
        )
    }
}

/**
 * Where a chat row's hairline starts, DERIVED from the row rather than picked.
 *
 * The mockup: the divider "is INDENTED so it begins where the text begins rather than at the screen
 * edge -- it does not run under the avatar". That point is the row's own horizontal padding, plus
 * the leading slot, plus the gap `SwetListRow` puts between the slot and the title. All three are
 * already stated in [SwetListRow]'s layout; this restates them in one place so that changing the
 * avatar size moves the rule with it.
 *
 * A FUNCTION AND NOT A CONSTANT for exactly that reason. Written as a number it would have been
 * correct on the day the avatar was 52dp and silently wrong the day it was not -- which is
 * [fabStackHeight]'s whole story, three screens over.
 */
fun chatRowDividerIndent(avatarSize: Dp): Dp = Space.two + avatarSize + Space.two
